/** Chip box: valid email + Enter/comma commits; ready for the next address. */
import { useState, type ClipboardEvent, type KeyboardEvent } from "react";
import { commitCcEmailTokens } from "@/lib/manufacturing/ccEmails";
import { inputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";

export function CcEmailChipInput(props: {
  emails: string[];
  onChange: (emails: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
}) {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  function apply(
    result: { ok: true; emails: string[] } | { ok: false; error: string },
    clearDraft: boolean
  ) {
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setError(null);
    props.onChange(result.emails);
    if (clearDraft) setDraft("");
  }

  function tryCommit(raw: string, clearDraft: boolean) {
    apply(commitCcEmailTokens(props.emails, raw), clearDraft);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (props.disabled) return;
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      tryCommit(draft, true);
      return;
    }
    if (e.key === "Backspace" && !draft && props.emails.length) {
      e.preventDefault();
      setError(null);
      props.onChange(props.emails.slice(0, -1));
    }
  }

  function onPaste(e: ClipboardEvent<HTMLInputElement>) {
    if (props.disabled) return;
    const text = e.clipboardData.getData("text");
    if (!text || !/[,;\s]/.test(text)) return;
    e.preventDefault();
    apply(commitCcEmailTokens(props.emails, `${draft} ${text}`), true);
  }

  function removeAt(index: number) {
    if (props.disabled) return;
    setError(null);
    props.onChange(props.emails.filter((_, i) => i !== index));
  }

  return (
    <div>
      <div
        className={`${inputClass} mt-1 flex min-h-[38px] flex-wrap items-center gap-1 py-1`}
        onClick={(e) => {
          const input = e.currentTarget.querySelector("input");
          input?.focus();
        }}
      >
        {props.emails.map((email, i) => (
          <span
            key={`${email}-${i}`}
            className="inline-flex max-w-full items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-900 dark:bg-indigo-900/40 dark:text-indigo-100"
          >
            <span className="truncate">{email}</span>
            <button
              type="button"
              className="rounded-full px-0.5 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 dark:text-indigo-200 dark:hover:bg-indigo-800"
              disabled={props.disabled}
              aria-label={`Remove ${email}`}
              onClick={(e) => {
                e.stopPropagation();
                removeAt(i);
              }}
            >
              ×
            </button>
          </span>
        ))}
        <input
          id={props.id}
          className="min-w-[10rem] flex-1 border-0 bg-transparent p-0 text-sm text-gray-900 outline-none dark:text-surface-ink"
          value={draft}
          disabled={props.disabled}
          placeholder={props.emails.length ? "" : props.placeholder ?? "name@example.com"}
          autoComplete="off"
          inputMode="email"
          onChange={(e) => {
            const v = e.target.value;
            if (v.includes(",")) {
              const lastComma = v.lastIndexOf(",");
              const toCommit = v.slice(0, lastComma);
              const rest = v.slice(lastComma + 1);
              const result = commitCcEmailTokens(props.emails, toCommit);
              if (!result.ok) {
                setError(result.error);
                setDraft(v);
                return;
              }
              setError(null);
              props.onChange(result.emails);
              setDraft(rest);
              return;
            }
            setDraft(v);
            if (error) setError(null);
          }}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onBlur={() => {
            if (draft.trim()) tryCommit(draft, true);
          }}
        />
      </div>
      {error ? <p className="mt-0.5 text-[11px] text-rose-700 dark:text-rose-300">{error}</p> : null}
    </div>
  );
}
